import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useApprovalRoleMappings,
  useUpdateApprovalRoleMappings,
} from '../../../../api/hooks/settings.hook';
import type { WorkspaceRole } from '../../../../api/services/settings';

const ROLE_OPTIONS: WorkspaceRole[] = ['viewer', 'editor', 'approver', 'admin'];

type RoleRow = {
  alias: string;
  role: WorkspaceRole;
};

export const ApprovalRolesConfiguration = () => {
  const mappings = useApprovalRoleMappings();
  const updateMappings = useUpdateApprovalRoleMappings();
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mappings.data) {
      const nextRows = Object.entries(mappings.data).map(([alias, role]) => ({
        alias,
        role: (role as WorkspaceRole) ?? 'approver',
      }));
      setRows(nextRows.length > 0 ? nextRows : [{ alias: '', role: 'approver' }]);
    }
  }, [mappings.data]);

  const handleAliasChange = (index: number, value: string) => {
    setRows((current) => {
      const copy = [...current];
      copy[index] = { ...copy[index], alias: value };
      return copy;
    });
  };

  const handleRoleChange = (index: number, role: WorkspaceRole) => {
    setRows((current) => {
      const copy = [...current];
      copy[index] = { ...copy[index], role };
      return copy;
    });
  };

  const handleRemove = (index: number) => {
    setRows((current) => {
      const copy = [...current];
      copy.splice(index, 1);
      return copy.length > 0 ? copy : [{ alias: '', role: 'approver' }];
    });
  };

  const handleAddRow = () => {
    setRows((current) => [...current, { alias: '', role: 'approver' }]);
  };

  const handleSave = async () => {
    const normalized: Record<string, WorkspaceRole> = {};
    for (const row of rows) {
      const alias = row.alias.trim().toLowerCase();
      if (!alias) {
        setError('Aliases must not be empty.');
        toast.error('Enter a name for each alias.');
        return;
      }
      if (!/^[a-z0-9][a-z0-9_-]*$/.test(alias)) {
        setError('Aliases may only contain letters, numbers, dashes, and underscores.');
        toast.error('Alias names may only contain letters, numbers, dashes, and underscores.');
        return;
      }
      if (normalized[alias]) {
        setError('Aliases must be unique.');
        toast.error('Aliases must be unique.');
        return;
      }
      normalized[alias] = row.role;
    }
    setError(null);
    try {
      await updateMappings.mutateAsync(normalized);
      toast.success('Approval role mappings saved.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update role mappings.';
      toast.error(message);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900">
            Approval role assignments
          </h1>
          <p className="text-sm text-neutral-600">
            Map approval policy roles to workspace roles to control who can approve sensitive
            actions.
          </p>
        </div>
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-4">
          {mappings.isLoading ? (
            <div className="text-sm text-neutral-500">Loading approval roles…</div>
          ) : (
            <div className="space-y-3">
              <div className="overflow-x-auto border border-neutral-200 rounded-lg">
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-neutral-600">
                        Approval alias
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-neutral-600">
                        Workspace role
                      </th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.alias}-${index}`} className="border-t border-neutral-200">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={row.alias}
                            onChange={(e) => handleAliasChange(index, e.target.value)}
                            placeholder="e.g. incident_manager"
                            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={row.role}
                            onChange={(e) =>
                              handleRoleChange(index, e.target.value as WorkspaceRole)
                            }
                            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-800"
                          >
                            {ROLE_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            className="text-xs text-neutral-600 hover:text-neutral-900"
                            onClick={() => handleRemove(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {error ? <p className="text-xs text-red-600">{error}</p> : null}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="text-sm text-neutral-600 hover:text-neutral-900"
                  onClick={handleAddRow}
                >
                  Add alias
                </button>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
                  onClick={handleSave}
                  disabled={updateMappings.isLoading}
                >
                  {updateMappings.isLoading ? 'Saving…' : 'Save mappings'}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
